import React, { useEffect, useState } from "react";
import { C } from "../constants.js";
import { I } from "../icons.jsx";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

export function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [fieldMap, setFieldMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedTemplate, setExpandedTemplate] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [regForm, setRegForm] = useState({ name:"", category:"LOR", stages:["New Case"], requires_provider:false, folder_path:"" });
  const [registering, setRegistering] = useState(false);
  const [versions, setVersions] = useState(null);
  const [versionsFor, setVersionsFor] = useState(null);
  // Cleanup UX: search + category filter + per-group collapse.
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState({});   // {category: true if collapsed}

  const fetchTemplates = () => {
    Promise.all([
      apiFetch("/api/templates"),
      apiFetch("/api/templates/fields"),
    ]).then(([tmplData, fieldData]) => {
      setTemplates(tmplData.data || []);
      setFieldMap(fieldData.data || {});
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { fetchTemplates(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true); setUploadResult(null);
    const form = new FormData();
    form.append("file", file);
    try {
      const resp = await fetch("/api/templates/upload", { method:"POST", body:form });
      if (resp.ok) {
        const data = await resp.json();
        setUploadResult(data);
        setRegForm(f => ({ ...f, name: file.name.replace(".docx","").replace(/form_/,"").replace(/_/g," ") }));
      }
    } catch (err) { console.error(err); }
    setUploading(false);
  };

  const handleRegister = async () => {
    if (!uploadResult || !regForm.name) return;
    setRegistering(true);
    try {
      await fetch("/api/templates/register", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({
          filename: uploadResult.filename,
          name: regForm.name,
          category: regForm.category,
          stages: regForm.stages,
          requires_provider: regForm.requires_provider,
          folder_path: regForm.folder_path || null,
          fields: uploadResult.placeholders,
          post_actions: [{ action:"attach_to_matter" }],
        }),
      });
      setShowUpload(false); setUploadResult(null);
      fetchTemplates();
    } catch (err) { console.error(err); }
    setRegistering(false);
  };

  const loadVersions = async (filename) => {
    setVersionsFor(filename);
    try {
      const resp = await fetch(`/api/templates/${encodeURIComponent(filename)}/versions`);
      if (resp.ok) setVersions(await resp.json());
    } catch (e) { console.error(e); }
  };

  const categoryColors = {
    LOR: { bg:"#EFF6FF", color:"#1D4ED8", border:"#BFDBFE" },
    Medical: { bg:"#F0FDF4", color:"#166534", border:"#BBF7D0" },
    Insurance: { bg:"#FFFBEB", color:"#92400E", border:"#FDE68A" },
    Intake: { bg:"#F5F3FF", color:"#6D28D9", border:"#DDD6FE" },
    Demand: { bg:"#FFF7ED", color:"#9A3412", border:"#FED7AA" },
    Settlement: { bg:"#F0FDFA", color:"#115E59", border:"#99F6E4" },
    Litigation: { bg:"#FDF2F8", color:"#9D174D", border:"#FBCFE8" },
  };
  const allCategories = ["LOR","Medical","Insurance","Intake","Demand","Settlement","Litigation","General"];
  const allStages = ["New Case","Treatment","Demand","Mediation","Litigation","Settlement"];
  const inputStyle = { padding:"8px 12px", borderRadius:"8px", border:`1px solid ${C.warmGray}`, fontSize:"13px", width:"100%" };

  // Upload modal
  const uploadModal = showUpload ? React.createElement("div", {
    style: { position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:5000, display:"flex", alignItems:"center", justifyContent:"center" },
    onClick: () => { setShowUpload(false); setUploadResult(null); },
  },
    React.createElement("div", {
      style: { background:"#fff", borderRadius:"16px", padding:"32px", maxWidth:"600px", width:"90%", maxHeight:"80vh", overflowY:"auto" },
      onClick: e => e.stopPropagation(),
    },
      React.createElement("h3", { style: { margin:"0 0 20px", color:C.navy, fontSize:"18px" } }, "Upload Template"),

      // Step 1: File upload
      !uploadResult ? React.createElement("div", null,
        React.createElement("label", {
          style: { display:"block", padding:"40px", border:`2px dashed ${C.warmGray}`, borderRadius:"12px", textAlign:"center", cursor:"pointer", background:C.cream },
        },
          React.createElement("input", { type:"file", accept:".docx", onChange:handleUpload, style:{ display:"none" } }),
          uploading ?
            React.createElement("span", { style: { color:C.textMuted } }, "Scanning template...") :
            React.createElement("div", null,
              React.createElement("div", { style: { fontSize:"32px", marginBottom:"8px" } }, "\uD83D\uDCC4"),
              React.createElement("div", { style: { color:C.navy, fontWeight:600, fontSize:"14px" } }, "Drop a .docx file here or click to browse"),
              React.createElement("div", { style: { color:C.textMuted, fontSize:"12px", marginTop:"4px" } }, "Placeholders like {{client_name}} will be auto-detected"),
            ),
        ),
      ) :
      // Step 2: Configure & register
      React.createElement("div", null,
        React.createElement("div", { style: { background:C.cream, borderRadius:"8px", padding:"14px", marginBottom:"20px" } },
          React.createElement("div", { style: { fontSize:"14px", fontWeight:600, color:C.navy } }, uploadResult.filename),
          React.createElement("div", { style: { fontSize:"12px", color:C.textMuted, marginTop:"4px" } },
            uploadResult.placeholder_count + " placeholders detected: " + (uploadResult.placeholders || []).join(", ")
          ),
          uploadResult.archived_previous ? React.createElement("div", { style: { fontSize:"12px", color:C.gold, marginTop:"4px" } },
            "Previous version archived as " + uploadResult.archived_previous
          ) : null,
        ),
        React.createElement("div", { style: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px", marginBottom:"16px" } },
          React.createElement("div", null,
            React.createElement("label", { style: { fontSize:"12px", color:C.textMuted, display:"block", marginBottom:"4px" } }, "Template Name"),
            React.createElement("input", { value:regForm.name, onChange:e => setRegForm(f => ({...f, name:e.target.value})), style:inputStyle }),
          ),
          React.createElement("div", null,
            React.createElement("label", { style: { fontSize:"12px", color:C.textMuted, display:"block", marginBottom:"4px" } }, "Category"),
            React.createElement("select", { value:regForm.category, onChange:e => setRegForm(f => ({...f, category:e.target.value})), style:inputStyle },
              allCategories.map(c => React.createElement("option", { key:c, value:c }, c)),
            ),
          ),
          React.createElement("div", null,
            React.createElement("label", { style: { fontSize:"12px", color:C.textMuted, display:"block", marginBottom:"4px" } }, "Clio Folder Path"),
            React.createElement("input", { value:regForm.folder_path, onChange:e => setRegForm(f => ({...f, folder_path:e.target.value})), placeholder:"e.g. 3 - Correspondence/FORMS", style:inputStyle }),
          ),
          React.createElement("div", null,
            React.createElement("label", { style: { fontSize:"12px", color:C.textMuted, display:"block", marginBottom:"4px" } }, "Per-Provider Document"),
            React.createElement("label", { style: { display:"flex", alignItems:"center", gap:"8px", fontSize:"13px", color:C.navy, cursor:"pointer", marginTop:"6px" } },
              React.createElement("input", { type:"checkbox", checked:regForm.requires_provider, onChange:e => setRegForm(f => ({...f, requires_provider:e.target.checked})) }),
              "Generate one copy per provider",
            ),
          ),
        ),
        React.createElement("div", { style: { marginBottom:"16px" } },
          React.createElement("label", { style: { fontSize:"12px", color:C.textMuted, display:"block", marginBottom:"6px" } }, "Stages"),
          React.createElement("div", { style: { display:"flex", flexWrap:"wrap", gap:"8px" } },
            allStages.map(s => React.createElement("label", { key:s, style: { display:"flex", alignItems:"center", gap:"4px", fontSize:"13px", color:C.navy, cursor:"pointer" } },
              React.createElement("input", { type:"checkbox", checked:regForm.stages.includes(s), onChange:e => {
                setRegForm(f => ({ ...f, stages: e.target.checked ? [...f.stages, s] : f.stages.filter(x => x !== s) }));
              }}),
              s,
            )),
          ),
        ),
        React.createElement("div", { style: { display:"flex", gap:"12px", justifyContent:"flex-end" } },
          React.createElement("button", { onClick:() => { setShowUpload(false); setUploadResult(null); },
            style: { padding:"10px 20px", borderRadius:"8px", border:`1px solid ${C.warmGray}`, background:"transparent", color:C.textMuted, cursor:"pointer", fontSize:"13px" },
          }, "Cancel"),
          React.createElement("button", { onClick:handleRegister, disabled:registering || !regForm.name,
            style: { padding:"10px 20px", borderRadius:"8px", border:"none", background:C.gold, color:"#fff", cursor:"pointer", fontWeight:600, fontSize:"13px" },
          }, registering ? "Registering..." : "Register Template"),
        ),
      ),
    ),
  ) : null;

  // Versions modal
  const versionsModal = versions ? React.createElement("div", {
    style: { position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:5000, display:"flex", alignItems:"center", justifyContent:"center" },
    onClick: () => setVersions(null),
  },
    React.createElement("div", {
      style: { background:"#fff", borderRadius:"16px", padding:"32px", maxWidth:"500px", width:"90%" },
      onClick: e => e.stopPropagation(),
    },
      React.createElement("h3", { style: { margin:"0 0 16px", color:C.navy } }, "Version History: " + versionsFor),
      versions.count === 0 ?
        React.createElement("p", { style: { color:C.textMuted, fontSize:"14px" } }, "No previous versions archived.") :
        React.createElement("div", { style: { display:"flex", flexDirection:"column", gap:"8px" } },
          (versions.versions || []).map((v, i) =>
            React.createElement("div", { key:i, style: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background:C.cream, borderRadius:"8px" } },
              React.createElement("div", null,
                React.createElement("div", { style: { fontSize:"13px", fontWeight:500, color:C.navy } }, v.filename),
                React.createElement("div", { style: { fontSize:"11px", color:C.textMuted } }, v.size_kb + " KB"),
              ),
              React.createElement("button", {
                onClick: async () => {
                  await fetch(`/api/templates/${encodeURIComponent(versionsFor)}/restore?archive_filename=${encodeURIComponent(v.filename)}`, { method:"POST" });
                  setVersions(null); fetchTemplates();
                },
                style: { padding:"6px 14px", borderRadius:"6px", border:`1px solid ${C.gold}`, background:"transparent", color:C.gold, cursor:"pointer", fontSize:"12px", fontWeight:500 },
              }, "Restore"),
            ),
          ),
        ),
      React.createElement("button", { onClick: () => setVersions(null),
        style: { marginTop:"16px", padding:"8px 20px", borderRadius:"8px", border:`1px solid ${C.warmGray}`, background:"transparent", color:C.textMuted, cursor:"pointer", fontSize:"13px" },
      }, "Close"),
    ),
  ) : null;

  return React.createElement("div", { className:"fade-in", style: { padding:"32px 40px", maxWidth:"1100px" } },
    uploadModal, versionsModal,

    // Header
    React.createElement("div", { style: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"24px" } },
      React.createElement("div", null,
        React.createElement("h1", {
          style: { fontFamily:"'Playfair Display',serif", fontSize:"28px", fontWeight:600, color:C.navy, marginBottom:"6px" }
        }, "Document Templates"),
        React.createElement("p", { style: { fontSize:"15px", color:C.textMuted, margin:0 } },
          templates.length + " templates registered"
        ),
      ),
      React.createElement("button", {
        onClick: () => setShowUpload(true),
        style: { padding:"10px 20px", borderRadius:"8px", border:"none", background:C.gold, color:"#fff", cursor:"pointer", fontWeight:600, fontSize:"13px" },
      }, "+ Upload Template"),
    ),

    (() => {
      if (loading) {
        return React.createElement("div", { style: { padding:"60px", textAlign:"center" } },
          React.createElement(Spinner, { size: 32 }));
      }

      // \u2500\u2500\u2500 Filtering \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
      const searchLc = search.trim().toLowerCase();
      const filtered = templates.filter(t => {
        if (categoryFilter && (t.category || "General") !== categoryFilter) return false;
        if (searchLc) {
          const hay = ((t.name || "") + " " + (t.filename || "") + " " + (t.category || "")).toLowerCase();
          if (!hay.includes(searchLc)) return false;
        }
        return true;
      });

      // \u2500\u2500\u2500 Group by category \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
      const byCategory = {};
      filtered.forEach(t => {
        const cat = t.category || "General";
        (byCategory[cat] = byCategory[cat] || []).push(t);
      });
      // Order: canonical categories first (in `allCategories`), any custom ones after.
      const orderedCategories = [
        ...allCategories.filter(c => byCategory[c]),
        ...Object.keys(byCategory).filter(c => !allCategories.includes(c)).sort(),
      ];
      // Available categories (for the filter dropdown) \u2014 regardless of search
      const allAvailableCats = [...new Set(templates.map(t => t.category || "General"))].sort();

      // \u2500\u2500\u2500 Filter bar \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
      const filterBar = React.createElement("div", {
        style: {
          display: "flex", gap: "10px", marginBottom: "16px",
          background: "#fff", padding: "10px 14px", borderRadius: "10px",
          border: `1px solid ${C.warmGray}`,
        }
      },
        React.createElement("input", {
          type: "search",
          value: search,
          onChange: e => setSearch(e.target.value),
          placeholder: "Search templates by name, filename, or category\u2026",
          style: {
            flex: 1, padding: "8px 12px", border: `1px solid ${C.warmGray}`,
            borderRadius: "6px", fontSize: "13px",
          }
        }),
        React.createElement("select", {
          value: categoryFilter,
          onChange: e => setCategoryFilter(e.target.value),
          style: {
            padding: "8px 12px", border: `1px solid ${C.warmGray}`,
            borderRadius: "6px", fontSize: "13px", minWidth: "150px",
            background: "#fff", color: C.textDark, cursor: "pointer",
          }
        },
          React.createElement("option", { value: "" }, "All categories"),
          ...allAvailableCats.map(c => React.createElement("option", { key: c, value: c }, c))
        ),
        (search || categoryFilter) && React.createElement("button", {
          onClick: () => { setSearch(""); setCategoryFilter(""); },
          style: {
            padding: "8px 12px", border: `1px solid ${C.warmGray}`,
            background: "#fff", borderRadius: "6px", fontSize: "12px",
            color: C.textMuted, cursor: "pointer",
          }
        }, "Clear"),
      );

      // \u2500\u2500\u2500 Empty states \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
      if (templates.length === 0) {
        return React.createElement("div", {
          style: { background:"#fff", borderRadius:"12px", border:`1px solid ${C.warmGray}`, padding:"48px", textAlign:"center" }
        },
          React.createElement("div", { style: { color:C.textMuted, fontSize:"14px" } }, "No templates configured yet."),
          React.createElement("button", {
            onClick: () => setShowUpload(true),
            style: { marginTop:"12px", padding:"10px 20px", borderRadius:"8px", border:"none", background:C.gold, color:"#fff", cursor:"pointer", fontWeight:600 },
          }, "Upload your first template"),
        );
      }

      if (filtered.length === 0) {
        return React.createElement("div", null,
          filterBar,
          React.createElement("div", {
            style: { background:"#fff", borderRadius:"12px", border:`1px solid ${C.warmGray}`, padding:"32px", textAlign:"center", color: C.textMuted, fontSize: "13px" }
          }, "No templates match those filters."),
        );
      }

      // \u2500\u2500\u2500 Grouped list \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
      return React.createElement("div", null,
        filterBar,
        ...orderedCategories.map(cat => {
          const catStyle = categoryColors[cat] || { bg: C.warmWhite, color: C.textDark, border: C.warmGray };
          const collapsed = !!collapsedGroups[cat];
          const items = byCategory[cat];
          return React.createElement("div", {
            key: cat,
            style: { marginBottom: "14px", background: "#fff", borderRadius: "10px", border: `1px solid ${C.warmGray}`, overflow: "hidden" }
          },
            // Category header (clickable to collapse)
            React.createElement("button", {
              onClick: () => setCollapsedGroups(prev => ({ ...prev, [cat]: !prev[cat] })),
              style: {
                width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "10px 16px", border: "none", background: catStyle.bg,
                cursor: "pointer", textAlign: "left",
              }
            },
              React.createElement("div", { style: { display: "flex", alignItems: "center", gap: "10px" } },
                React.createElement("span", {
                  style: {
                    fontSize: "11px", fontWeight: 600, color: catStyle.color,
                    textTransform: "uppercase", letterSpacing: "0.06em",
                  }
                }, cat),
                React.createElement("span", {
                  style: { fontSize: "11px", color: C.textMuted }
                }, `${items.length}`),
              ),
              React.createElement("span", {
                style: {
                  color: catStyle.color, fontSize: "12px",
                  transform: collapsed ? "rotate(-90deg)" : "none",
                  transition: "transform 0.15s",
                }
              }, "\u25BC"),
            ),

            // Items in this group
            !collapsed && React.createElement("div", null,
              ...items.map((tmpl, i) => {
                const isExpanded = expandedTemplate === tmpl.filename;
                const fields = tmpl.fields || [];
                return React.createElement("div", {
                  key: tmpl.filename || i,
                  style: { borderTop: `1px solid ${C.warmGray}60` }
                },
                  // Dense row header (single-line summary)
                  React.createElement("button", {
                    onClick: () => setExpandedTemplate(isExpanded ? null : tmpl.filename),
                    style: {
                      width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                      gap: "12px", padding: "10px 16px", border: "none", background: "transparent",
                      cursor: "pointer", textAlign: "left",
                    },
                    onMouseEnter: e => { e.currentTarget.style.background = C.warmWhite; },
                    onMouseLeave: e => { e.currentTarget.style.background = "transparent"; },
                  },
                    React.createElement("div", { style: { minWidth: 0, flex: 1 } },
                      React.createElement("div", {
                        style: { fontSize: "13px", fontWeight: 600, color: C.navy,
                                 whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }
                      }, tmpl.name || tmpl.filename),
                      React.createElement("div", {
                        style: { fontSize: "11px", color: C.textMuted, marginTop: "2px",
                                 whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }
                      },
                        tmpl.filename +
                        " \u00B7 " + fields.length + " field" + (fields.length === 1 ? "" : "s") +
                        (tmpl.requires_provider ? " \u00B7 per-provider" : "") +
                        (tmpl.stages && tmpl.stages.length ? " \u00B7 " + tmpl.stages.join(", ") : "")
                      ),
                    ),
                    React.createElement("span", {
                      style: {
                        color: C.textLight, fontSize: "12px",
                        transform: isExpanded ? "rotate(90deg)" : "none",
                        transition: "transform 0.15s",
                      }
                    }, "\u25B6"),
                  ),

                  // Expanded detail
                  isExpanded && React.createElement("div", {
                    className: "slide-down",
                    style: { padding: "12px 16px 16px", background: C.warmWhite }
                  },
                    React.createElement("div", { style: { marginBottom: "12px" } },
                      React.createElement("div", {
                        style: { fontSize: "10px", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, marginBottom: "6px" }
                      }, "Required Fields"),
                      React.createElement("div", { style: { display: "flex", flexWrap: "wrap", gap: "6px" } },
                        fields.map(f => {
                          const meta = fieldMap[f] || {};
                          return React.createElement("div", {
                            key: f,
                            style: {
                              padding: "4px 10px", borderRadius: "6px", fontSize: "12px",
                              background: meta.sensitive ? C.errorBg : `${C.navy}08`,
                              border: `1px solid ${meta.sensitive ? "#FECACA" : C.warmGray}`,
                              color: meta.sensitive ? C.error : C.navy, fontWeight: 500,
                            },
                            title: meta.label ? `${meta.label} (source: ${meta.source || "unknown"})` : f,
                          }, meta.label || f, meta.sensitive && " \uD83D\uDD12");
                        }),
                      ),
                    ),

                    tmpl.post_actions && tmpl.post_actions.length > 0 && React.createElement("div", { style: { marginBottom: "12px" } },
                      React.createElement("div", {
                        style: { fontSize: "10px", color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, marginBottom: "6px" }
                      }, "Post-Generation Actions"),
                      tmpl.post_actions.map((action, j) => {
                        const labels = {
                          attach_to_matter: "Attach document to matter in Clio",
                          mark_task_complete: `Mark task complete (matching: "${action.task_name_contains}")`,
                          create_followup_task: `Create follow-up task: "${action.name}" in ${action.days} days`,
                        };
                        return React.createElement("div", {
                          key: j,
                          style: { display: "flex", alignItems: "center", gap: "6px", padding: "2px 0", fontSize: "12px", color: C.textDark }
                        },
                          React.createElement("span", { style: { color: C.success } }, I.check),
                          labels[action.action] || action.action,
                        );
                      }),
                    ),

                    React.createElement("button", {
                      onClick: () => loadVersions(tmpl.filename),
                      style: { padding: "5px 12px", borderRadius: "6px", border: `1px solid ${C.warmGray}`, background: "#fff", color: C.textMuted, cursor: "pointer", fontSize: "11px" },
                    }, "Version History"),
                  ),
                );
              }),
            ),
          );
        }),
      );
    })(),
  );
}

